import React, { useEffect, useMemo, useState } from 'react';
import { Send } from 'lucide-react';
import { toast } from 'sonner';

import { adminApi, broadcastApi } from '../../api/examApi';
import { parseApiError } from '../../lib/apiErrors';

const ADMIN_TABS = [
  { key: 'students', label: 'Students' },
  { key: 'groups', label: 'Groups' },
  { key: 'everyone', label: 'Everyone' },
];

/**
 * Shared broadcast compose UI for admin and faculty.
 * Admin: full target picker (students / groups / everyone) + include_faculty.
 * Faculty: groups only, restricted server-side to their own faculty_groups.
 */
export default function BroadcastComposer({ role }) {
  const isAdmin = role === 'admin';
  const [tab, setTab] = useState('groups');

  const [message, setMessage] = useState('');

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(isAdmin);
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());

  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());

  const [everyoneOn, setEveryoneOn] = useState(false);
  const [includeFaculty, setIncludeFaculty] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      adminApi
        .listStudents({ registration_status: 'approved', is_active: true, page_size: 100 })
        .then((res) => setStudents(res.data.data))
        .catch(() => toast.error('Failed to load students'))
        .finally(() => setStudentsLoading(false));
    }
    broadcastApi
      .getEligibleGroups()
      .then((res) => setGroups(res.data))
      .catch(() => toast.error('Failed to load groups'))
      .finally(() => setGroupsLoading(false));
  }, [isAdmin]);

  const filteredStudents = useMemo(() => {
    const term = studentSearch.trim().toLowerCase();
    if (!term) return students;
    return students.filter(
      (student) => student.name.toLowerCase().includes(term) || student.email.toLowerCase().includes(term)
    );
  }, [students, studentSearch]);

  const toggleStudent = (studentId) => {
    setSelectedStudentIds((current) => {
      const next = new Set(current);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  };

  const toggleGroup = (groupId) => {
    setSelectedGroupIds((current) => {
      const next = new Set(current);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const canSubmit =
    message.trim().length > 0 && (everyoneOn || selectedStudentIds.size > 0 || selectedGroupIds.size > 0);

  const summaryText = everyoneOn
    ? 'All approved students selected'
    : `${isAdmin ? `${selectedStudentIds.size} student(s), ` : ''}${selectedGroupIds.size} group(s) selected`;

  const handleSend = async () => {
    if (!canSubmit) return;
    setSending(true);
    try {
      const payload = {
        message: message.trim(),
        targets: everyoneOn
          ? { student_ids: [], group_ids: [], all: true }
          : { student_ids: [...selectedStudentIds], group_ids: [...selectedGroupIds], all: false },
        include_faculty: includeFaculty,
      };
      const { data } = await broadcastApi.send(payload);
      toast.success(
        `Sent to ${data.student_recipient_count} student(s)${
          data.faculty_recipient_count > 0 ? ` and ${data.faculty_recipient_count} faculty` : ''
        }.`
      );
      setMessage('');
      setSelectedStudentIds(new Set());
      setSelectedGroupIds(new Set());
      setEveryoneOn(false);
      setIncludeFaculty(false);
    } catch (error) {
      toast.error(parseApiError(error, 'Failed to send broadcast').message);
    } finally {
      setSending(false);
    }
  };

  const tabs = isAdmin ? ADMIN_TABS : ADMIN_TABS.filter((t) => t.key === 'groups');

  return (
    <div data-testid="broadcast-composer" className="max-w-2xl">
      <div className="mb-4">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Message</label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={4}
          placeholder="What do you want to tell them?"
          data-testid="broadcast-message"
          className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
        />
      </div>

      {tabs.length > 1 && (
        <div className="mb-4 flex gap-1 rounded-md border border-slate-200 p-1 dark:border-slate-700">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`broadcast-tab-${t.key}`}
              className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                tab === t.key
                  ? 'bg-primary text-white'
                  : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {isAdmin && tab === 'students' && (
        <div className={everyoneOn ? 'pointer-events-none opacity-50' : ''}>
          <input
            type="text"
            placeholder="Search students by name or email"
            value={studentSearch}
            onChange={(event) => setStudentSearch(event.target.value)}
            disabled={everyoneOn}
            data-testid="broadcast-student-search"
            className="mb-3 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
          />
          <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 dark:border-slate-700">
            {studentsLoading ? (
              <p className="p-2 text-sm text-slate-500 dark:text-slate-400">Loading students…</p>
            ) : filteredStudents.length === 0 ? (
              <p className="p-2 text-sm text-slate-500 dark:text-slate-400">No approved students found.</p>
            ) : (
              filteredStudents.map((student) => (
                <label
                  key={student.id}
                  data-testid={`broadcast-student-${student.id}`}
                  className="flex items-center gap-2 rounded-md p-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50"
                >
                  <input
                    type="checkbox"
                    disabled={everyoneOn}
                    checked={selectedStudentIds.has(student.id)}
                    onChange={() => toggleStudent(student.id)}
                  />
                  <span>
                    {student.name} ({student.email})
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {tab === 'groups' && (
        <div className={everyoneOn ? 'pointer-events-none opacity-50' : ''}>
          <div className="max-h-72 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-2 dark:border-slate-700">
            {groupsLoading ? (
              <p className="p-2 text-sm text-slate-500 dark:text-slate-400">Loading groups…</p>
            ) : groups.length === 0 ? (
              <p className="p-2 text-sm text-slate-500 dark:text-slate-400">
                {isAdmin ? 'No groups created yet.' : 'You are not assigned to any groups.'}
              </p>
            ) : (
              groups.map((group) => (
                <label
                  key={group.id}
                  data-testid={`broadcast-group-${group.id}`}
                  className="flex items-center gap-2 rounded-md p-2 text-sm text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/50"
                >
                  <input
                    type="checkbox"
                    disabled={everyoneOn}
                    checked={selectedGroupIds.has(group.id)}
                    onChange={() => toggleGroup(group.id)}
                  />
                  <span>
                    {group.name} ({group.member_count} member{group.member_count === 1 ? '' : 's'})
                  </span>
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {isAdmin && tab === 'everyone' && (
        <div className="rounded-md border border-slate-200 p-4 dark:border-slate-700">
          <label className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={everyoneOn}
              onChange={() => setEveryoneOn((v) => !v)}
              data-testid="broadcast-everyone-toggle"
            />
            Send to all approved students
          </label>
        </div>
      )}

      <label className="mt-4 flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
        <input
          type="checkbox"
          checked={includeFaculty}
          onChange={() => setIncludeFaculty((v) => !v)}
          data-testid="broadcast-include-faculty"
        />
        Also notify faculty assigned to the targeted group(s)
        {!isAdmin && ' (including yourself, if included)'}
      </label>

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="broadcast-summary">
          {summaryText}
        </p>
        <button
          onClick={handleSend}
          disabled={!canSubmit || sending}
          data-testid="broadcast-submit"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-light disabled:opacity-50"
        >
          <Send size={16} />
          {sending ? 'Sending...' : 'Send Broadcast'}
        </button>
      </div>
    </div>
  );
}
